import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

// Global AbortController for cancelling in-flight requests
let currentAbortController = null;

/**
 * Get or create an AbortController for the current session
 */
const getAbortController = () => {
  if (!currentAbortController) {
    currentAbortController = new AbortController();
  }
  return currentAbortController;
};

/**
 * Abort all pending requests and create a fresh controller
 * Call this when user resets session or navigates away
 */
export const abortAllRequests = () => {
  if (currentAbortController) {
    currentAbortController.abort();
    currentAbortController = null;
  }
  // Create new controller for future requests
  currentAbortController = new AbortController();
};
// Configure axios defaults
axios.defaults.baseURL = API;

// Exponential backoff retry logic
axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const config = error.config;

    // Don't retry if request was cancelled
    if (axios.isCancel(error)) {
      return Promise.reject(error);
    }

    // Initialize retry count
    if (!config.retryCount) {
      config.retryCount = 0;
    }

    const MAX_RETRIES = 3;

    // Check if we should retry (5xx Server Errors only, NOT 429 Rate Limits)
    // We intentionally exclude 429 so the UI can show the error immediately and stop the loop
    if (config.retryCount < MAX_RETRIES &&
      (error.response?.status >= 500 && error.response?.status < 600)) {

      // Retry logic: exponential backoff
      config.retryCount += 1;

      // Calculate delay: 1s, 2s, 4s...
      const delay = Math.pow(2, config.retryCount - 1) * 1000;

      // Wait for the delay
      await new Promise(resolve => setTimeout(resolve, delay));

      // Retry the request
      return axios(config);
    }

    return Promise.reject(error);
  }
);

export const api = {
  // Session management
  createSession: async () => {
    const response = await axios.post(`${API}/sessions`, {}, {
      signal: getAbortController().signal
    });
    return response.data;
  },

  getSession: async (sessionId) => {
    const response = await axios.get(`${API}/sessions/${sessionId}`, {
      signal: getAbortController().signal
    });
    return response.data;
  },

  // Video upload
  uploadVideo: async (sessionId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await axios.post(`${API}/upload-video/${sessionId}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      signal: getAbortController().signal
    });
    return response.data;
  },

  // Device analysis
  analyzeDevice: async (sessionId) => {
    const response = await axios.post(`${API}/analyze-device/${sessionId}`, {}, {
      signal: getAbortController().signal
    });
    return response.data;
  },

  // Manual upload
  uploadManual: async (sessionId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await axios.post(`${API}/upload-manual/${sessionId}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      signal: getAbortController().signal
    });
    return response.data;
  },

  // Manual search
  searchManual: async (sessionId) => {
    const response = await axios.post(`${API}/search-manual/${sessionId}`, {}, {
      signal: getAbortController().signal
    });
    return response.data;
  },

  // Diagnosis
  diagnose: async (sessionId) => {
    const response = await axios.post(`${API}/diagnose/${sessionId}`, {}, {
      signal: getAbortController().signal
    });
    return response.data;
  },

  // Safety check
  safetyCheck: async (sessionId) => {
    const response = await axios.post(`${API}/safety-check/${sessionId}`, {}, {
      signal: getAbortController().signal
    });
    return response.data;
  },

  // Generate repair steps
  generateRepairSteps: async (sessionId, forceProceed = false) => {
    const response = await axios.post(`${API}/generate-repair-steps/${sessionId}?force_proceed=${forceProceed}`, {}, {
      signal: getAbortController().signal
    });
    return response.data;
  },

  // AR Guidance
  getARGuidance: async (sessionId, step, imageBase64) => {
    const response = await axios.post(`${API}/ar-guidance/${sessionId}`, {
      step: step,
      image: imageBase64
    }, {
      signal: getAbortController().signal
    });
    return response.data;
  },
};

export default api;

